import {
  Box,
  Card,
  CardContent,
  Typography,
  Tooltip as MuiTooltip,
  IconButton,
} from '@mui/material';
import { format, parseISO, startOfMonth } from 'date-fns';
import { ja } from 'date-fns/locale';
import { HelpCircle } from 'lucide-react';
import React, { useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DailyShelfMetricsV2 } from '../../../services/shelf-service';
import { getPageTypeColor } from '../../../utils/page-type-utils';
import { TimeUnit } from './TimeUnitSelector';

interface PageTypeMetricsChartsProps {
  data: DailyShelfMetricsV2[];
  compareData?: DailyShelfMetricsV2[];
  showComparison?: boolean;
  timeUnit: TimeUnit;
}

interface ChartDataItem {
  date: string;
  [key: string]: string | number | boolean;
}

const PageTypeMetricsCharts: React.FC<PageTypeMetricsChartsProps> = ({
  data,
  compareData = [],
  showComparison = false,
  timeUnit,
}) => {
  const sortedPageTypes = useMemo(() => {
    const allData = [...data, ...(showComparison ? compareData : [])];
    const pageTypeShownTotals = allData.reduce(
      (acc, curr) => {
        acc[curr.page_name] = (acc[curr.page_name] || 0) + curr.shown_ss;
        return acc;
      },
      {} as { [key: string]: number },
    );

    return Object.entries(pageTypeShownTotals)
      .sort(([, a], [, b]) => b - a)
      .map(([pageType]) => pageType);
  }, [data, compareData, showComparison]);

  const charts = [
    {
      title: 'ページ別シェルフIMP SS数',
      dataKey: 'shown_ss',
      tooltip: (
        <Box sx={{ maxWidth: 280 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            シェルフが表示されたセッション数をページタイプごとに集計した値。
          </Typography>
          <Typography variant="body2">
            合計値はページ間の重複のため
            <Typography
              component="span"
              sx={{
                fontWeight: 'bold',
              }}
            >
              全体集計より大きな値
            </Typography>
            となることに注意
          </Typography>
        </Box>
      ),
    },
    {
      title: 'ページ別シェルフクリックSS数',
      dataKey: 'click_ss',
      tooltip: (
        <Box sx={{ maxWidth: 280 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            シェルフをクリックしたセッション数を ページタイプごとに集計した値。
          </Typography>
          <Typography variant="body2">
            合計値はページ間の重複のため
            <Typography
              component="span"
              sx={{
                fontWeight: 'bold',
              }}
            >
              全体集計より大きな値
            </Typography>
            となることに注意
          </Typography>
        </Box>
      ),
    },
    {
      title: 'ページ別シェルフ経由CV SS数',
      dataKey: 'shelf_cv_ss',
      tooltip: (
        <Box sx={{ maxWidth: 280 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            シェルフをクリックした後に同じセッション内で購入に至ったセッション数をページタイプごとに集計した値。
          </Typography>
          <Typography variant="body2">
            合計値はページ間の重複のため
            <Typography
              component="span"
              sx={{
                fontWeight: 'bold',
              }}
            >
              全体集計より大きな値
            </Typography>
            となることに注意
          </Typography>
        </Box>
      ),
    },
  ];

  const getTimeUnitStartDate = (date: string) => {
    const dateObj = parseISO(date);
    switch (timeUnit) {
      case 'week': {
        // 全データの中で最新の日付を取得
        const allData = [...data, ...(showComparison ? compareData : [])];
        const maxDate = parseISO(
          allData.reduce(
            (max, curr) => (curr.ymd > max ? curr.ymd : max),
            allData[0].ymd,
          ),
        );
        // 最新の日付から何週目かを計算
        const diffDays = Math.floor(
          (maxDate.getTime() - dateObj.getTime()) / (1000 * 60 * 60 * 24),
        );
        const weekNumber = Math.floor(diffDays / 7);
        // 最新の日付からweekNumber週前の日付を計算
        const weekStartDate = new Date(maxDate);
        weekStartDate.setDate(weekStartDate.getDate() - weekNumber * 7);
        return format(weekStartDate, 'yyyy-MM-dd');
      }
      case 'month':
        return format(startOfMonth(dateObj), 'yyyy-MM-dd');
      default:
        return date;
    }
  };

  const processData = (metrics: DailyShelfMetricsV2[], metric: string) => {
    // 週次表示の場合、各週に含まれる日数もカウント
    const groupedData = metrics.reduce(
      (acc, curr) => {
        const date = curr.ymd;
        const key = getTimeUnitStartDate(date);

        if (!acc[key]) {
          acc[key] = {
            date: key,
            dayCount: 0,
            minDate: date,
            maxDate: date,
          } as ChartDataItem & {
            dayCount: number;
            minDate: string;
            maxDate: string;
          };
          sortedPageTypes.forEach((pageType) => {
            acc[key][pageType] = 0;
          });
        }

        acc[key][curr.page_name] =
          (Number(acc[key][curr.page_name]) || 0) + Number(curr[metric]);

        // 日数をカウント（同じ日付は1回だけカウント）
        if (!acc[key][`_counted_${date}`]) {
          acc[key].dayCount += 1;
          acc[key][`_counted_${date}`] = true;
        }

        // 実際の日付範囲を更新
        if (date < acc[key].minDate) acc[key].minDate = date;
        if (date > acc[key].maxDate) acc[key].maxDate = date;

        return acc;
      },
      {} as Record<
        string,
        ChartDataItem & { dayCount: number; minDate: string; maxDate: string }
      >,
    );

    // 週次表示でも1週間未満のデータを含めて表示
    const filteredData = Object.values(groupedData).filter((data) => {
      if (timeUnit === 'week') {
        return data.dayCount >= 1; // 1日以上のデータがあれば表示
      }
      return true; // 日次・月次表示の場合はフィルタリングしない
    });

    // dayCountと_counted_*プロパティを削除し、minDate、maxDateは保持
    const cleanedData = filteredData.map((data) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { dayCount, ...cleanData } = data;
      // _counted_で始まるプロパティを削除
      Object.keys(cleanData).forEach((key) => {
        if (key.startsWith('_counted_')) {
          delete cleanData[key];
        }
      });
      return cleanData as ChartDataItem & { minDate: string; maxDate: string };
    });

    return cleanedData.sort((a, b) => a.date.localeCompare(b.date));
  };

  // 月次表示の場合は同じ月のデータを集約
  const mergeMonthlyData = (data: ChartDataItem[]) => {
    if (timeUnit !== 'month') return data;

    const monthlyData = data.reduce(
      (acc, curr) => {
        const monthKey = curr.date.substring(0, 7); // YYYY-MM
        if (!acc[monthKey]) {
          acc[monthKey] = {
            ...curr,
            date: curr.date, // 月の最初の日付を保持
          };
          return acc;
        }

        // 同じ月のデータを集約
        sortedPageTypes.forEach((pageType) => {
          const currentValue = Number(acc[monthKey][pageType]) || 0;
          const addValue = Number(curr[pageType]) || 0;
          acc[monthKey][pageType] = currentValue + addValue;
        });

        return acc;
      },
      {} as Record<string, ChartDataItem>,
    );

    return Object.values(monthlyData).sort((a, b) =>
      a.date.localeCompare(b.date),
    );
  };

  const formatXAxisTick = (date: string) => {
    switch (timeUnit) {
      case 'week':
        return format(parseISO(date), 'M/d週', { locale: ja });
      case 'month':
        return format(parseISO(date), 'yyyy/M月', { locale: ja });
      default:
        return format(parseISO(date), 'M/d', { locale: ja });
    }
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 2,
      }}
    >
      {charts.map((chart) => {
        const mainData = processData(data, chart.dataKey);
        const compareData2 = showComparison
          ? processData(compareData, chart.dataKey)
          : [];
        const chartData = mergeMonthlyData(
          [...mainData, ...compareData2].sort((a, b) =>
            a.date.localeCompare(b.date),
          ),
        );

        // ツールチップで実際の日付範囲を参照するためのマップ
        const dateRangeMap = new Map<
          string,
          { minDate: string; maxDate: string }
        >();
        chartData.forEach((item) => {
          if ('minDate' in item && 'maxDate' in item) {
            dateRangeMap.set(item.date, {
              minDate: item.minDate as string,
              maxDate: item.maxDate as string,
            });
          }
        });

        return (
          <Card
            key={chart.title}
            sx={{
              height: '100%',
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              bgcolor: '#FFFFFF',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    textAlign: 'left',
                  }}
                >
                  {chart.title}
                </Typography>
                {chart.tooltip && (
                  <MuiTooltip title={chart.tooltip} arrow placement="top">
                    <IconButton
                      size="small"
                      sx={{
                        color: 'text.secondary',
                        padding: '2px',
                        '&:hover': {
                          color: 'primary.main',
                        },
                      }}
                    >
                      <HelpCircle size={16} />
                    </IconButton>
                  </MuiTooltip>
                )}
              </Box>
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <AreaChart
                    data={chartData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 0,
                      bottom: 60,
                    }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="rgba(0, 0, 0, 0.1)"
                    />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatXAxisTick}
                      tick={{ fontSize: 12, fill: '#666' }}
                      tickLine={{ stroke: '#ccc' }}
                      axisLine={{ stroke: '#ccc' }}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: '#666' }}
                      tickLine={{ stroke: '#ccc' }}
                      axisLine={{ stroke: '#ccc' }}
                    />
                    <Tooltip
                      formatter={(value: number) => value.toLocaleString()}
                      labelFormatter={(label: string) => {
                        const date = parseISO(label);
                        const dateRange = dateRangeMap.get(label);

                        switch (timeUnit) {
                          case 'week': {
                            // 実際のデータ範囲を使用
                            if (dateRange) {
                              const startDate = parseISO(dateRange.minDate);
                              const endDate = parseISO(dateRange.maxDate);
                              return `${format(startDate, 'yyyy/M/d(E)', { locale: ja })}~${format(endDate, 'M/d(E)', { locale: ja })}`;
                            }
                            // フォールバック: 週の開始日を計算（終了日-6日）
                            const startDate = new Date(date);
                            startDate.setDate(startDate.getDate() - 6);
                            return `${format(startDate, 'yyyy/M/d(E)', { locale: ja })}~${format(date, 'M/d(E)', { locale: ja })}`;
                          }
                          case 'month': {
                            // 実際のデータ範囲を使用
                            if (dateRange) {
                              const startDate = parseISO(dateRange.minDate);
                              const endDate = parseISO(dateRange.maxDate);
                              return `${format(startDate, 'yyyy/M/d', { locale: ja })}~${format(endDate, 'M/d', { locale: ja })}`;
                            }
                            // フォールバック: 月の最後の日を計算
                            const endDate = new Date(
                              date.getFullYear(),
                              date.getMonth() + 1,
                              0,
                            );
                            return `${format(date, 'yyyy/M/d', { locale: ja })}~${format(endDate, 'M/d', { locale: ja })}`;
                          }
                          default:
                            return format(date, 'yyyy/M/d(E)', { locale: ja });
                        }
                      }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{
                        fontSize: '11px',
                      }}
                    />
                    {sortedPageTypes.map((pageType) => {
                      const color = getPageTypeColor(pageType);
                      return (
                        <Area
                          key={pageType}
                          type="monotone"
                          dataKey={pageType}
                          name={pageType}
                          stackId="1"
                          stroke={color}
                          fill={color}
                          fillOpacity={0.3}
                        />
                      );
                    })}
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

export default PageTypeMetricsCharts;
